import {Site} from "../../../../store/branches/sites/stateTypes";

export type ExplorerNodeType = "site" | "device" | "group" | "sensor";

// Matches the classification returned by GET /api/v1/devices/status-rollup/ —
// offline takes priority over a lingering alarm/warning (stale sensor data
// once a device is offline), alarm over warning.
export type DeviceStatus = "healthy" | "offline" | "warning" | "alarm";

export interface StatusCounts {
    healthy: number;
    offline: number;
    warning: number;
    alarm: number;
}

export const STATUS_LABELS: Record<DeviceStatus, string> = {
    healthy: "Healthy",
    offline: "Offline",
    warning: "Warning",
    alarm: "Alarm",
};

// One entry per active alarm/warning Notification on a device — sensorId is
// null for a merged/group-level event, in which case nodeId identifies the
// enclosing group instead (see GET /api/v1/devices/status-rollup/).
export interface ProblemSensor {
    sensorId: number | null;
    nodeId: number | null;
    name: string;
    status: DeviceStatus;
}

export interface ExplorerNode {
    key: string;
    label: string;
    nodeType: ExplorerNodeType;
    locationId: number;
    deviceId?: number;
    childCount?: number;
    children?: ExplorerNode[];
}

// Shape of `GET /api/v1/devices/{id}/sensors/` (Device.get_sensors -> generate_sensors_data).
// Not to be confused with the capitalized WsGroup/WsSensor shape used by the live websocket relay.
export interface SensorApiSensor {
    id: string;
    name: string;
    unit?: string;
}

export interface SensorApiGroup {
    id: string;
    name: string;
    sensors: SensorApiSensor[];
    groups?: SensorApiGroup[];
}

const sensorNode = (sensor: SensorApiSensor, locationId: number, deviceId: number): ExplorerNode => ({
    key: `sensor-${sensor.id}`,
    label: sensor.name ?? "",
    nodeType: "sensor",
    locationId,
    deviceId,
});

const groupNode = (group: SensorApiGroup, locationId: number, deviceId: number): ExplorerNode => {
    const children = [
        ...(group.groups || []).map((subGroup) => groupNode(subGroup, locationId, deviceId)),
        ...(group.sensors || []).map((sensor) => sensorNode(sensor, locationId, deviceId)),
    ];
    return {
        key: `group-${group.id}`,
        label: group.name ?? "",
        nodeType: "group",
        locationId,
        deviceId,
        childCount: children.length,
        children,
    };
};

export const buildSensorTree = (groups: SensorApiGroup[], locationId: number, deviceId: number): ExplorerNode[] =>
    (groups || []).map((group) => groupNode(group, locationId, deviceId));

export const countLeafSensors = (nodes: ExplorerNode[]): number =>
    nodes.reduce((sum, node) => (
        node.nodeType === "sensor" ? sum + 1 : sum + countLeafSensors(node.children || [])
    ), 0);

export const updateNodeChildren = (list: ExplorerNode[], key: string, children: ExplorerNode[]): ExplorerNode[] =>
    list.map((node) => {
        if (node.key === key) {
            return {...node, children, childCount: countLeafSensors(children)};
        }
        if (node.children) {
            return {...node, children: updateNodeChildren(node.children, key, children)};
        }
        return node;
    });

const matchesQuery = (label: string, query: string) => label.toLowerCase().includes(query.toLowerCase());

export const filterTree = (nodes: ExplorerNode[], query: string): ExplorerNode[] => {
    if (!query) {
        return nodes;
    }

    return nodes.reduce<ExplorerNode[]>((result, node) => {
        const filteredChildren = node.children ? filterTree(node.children, query) : undefined;
        const selfMatches = matchesQuery(node.label, query);

        if (selfMatches) {
            result.push(node);
        } else if (filteredChildren && filteredChildren.length > 0) {
            result.push({...node, children: filteredChildren});
        }

        return result;
    }, []);
};

// Site -> device level only, deliberately not recursive into group/sensor
// children — status is a device-level concept (see DEVICES_EXPLORER.md), so
// once a device passes the filter its already-loaded sensor subtree is left
// untouched rather than being pruned by a concept that doesn't apply to it.
//
// A device the user has manually expanded stays visible even if a live
// status update means it no longer matches the active filter — otherwise a
// device you're actively looking at (and everything open inside it) would
// vanish out from under you the moment its warning/alarm changes, which
// reads as the explorer randomly closing things on its own.
export const filterTreeByStatus = (
    nodes: ExplorerNode[],
    activeStatus: DeviceStatus | null,
    deviceStatus: Record<number, DeviceStatus>,
    expanded: Set<string>,
): ExplorerNode[] => {
    if (!activeStatus) {
        return nodes;
    }

    return nodes.reduce<ExplorerNode[]>((result, node) => {
        if (node.nodeType !== "site") {
            result.push(node);
            return result;
        }

        const children = (node.children || []).filter(
            (device) => device.deviceId !== undefined
                && (deviceStatus[device.deviceId] === activeStatus || expanded.has(device.key)),
        );
        if (children.length > 0) {
            result.push({...node, children});
        }
        return result;
    }, []);
};

const emptyStatusCounts = (): StatusCounts => ({healthy: 0, offline: 0, warning: 0, alarm: 0});

// Pure client-side aggregation over data already in hand (site->device
// membership from the sites fetch, device->status from the rollup fetch) —
// deliberately not computed server-side, see DEVICES_EXPLORER.md.
export const aggregateStatusCounts = (
    sites: Site[],
    deviceStatus: Record<number, DeviceStatus>,
): { bySite: Record<number, StatusCounts>; totals: StatusCounts } => {
    const bySite: Record<number, StatusCounts> = {};
    const totals = emptyStatusCounts();

    (sites || []).forEach((site) => {
        if (site.id === undefined) {
            return;
        }
        const counts = emptyStatusCounts();
        (site.devices || []).forEach((device) => {
            const value = device.id !== undefined ? deviceStatus[device.id] : undefined;
            if (!value) {
                return;
            }
            counts[value] += 1;
            totals[value] += 1;
        });
        bySite[site.id] = counts;
    });

    return {bySite, totals};
};

// Same node-key scheme buildSensorTree uses: a specific sensor when known,
// falling back to its enclosing group for merged/group-level events.
export const problemSensorNodeKey = (problem: ProblemSensor): string | undefined => {
    if (problem.sensorId !== null && problem.sensorId !== undefined) {
        return `sensor-${problem.sensorId}`;
    }
    if (problem.nodeId !== null && problem.nodeId !== undefined) {
        return `group-${problem.nodeId}`;
    }
    return undefined;
};

// Flattens problemSensors into the same key scheme used by ExplorerNode.key,
// so ExplorerTree can look up a status for any group/sensor row the same way
// it already does for device rows.
export const buildProblemNodeStatus = (
    problemSensors: Record<number, ProblemSensor[]>,
): Record<string, DeviceStatus> => {
    const result: Record<string, DeviceStatus> = {};
    Object.values(problemSensors).forEach((problems) => {
        problems.forEach((problem) => {
            const key = problemSensorNodeKey(problem);
            if (key) {
                result[key] = problem.status;
            }
        });
    });
    return result;
};

// Ancestor keys (not including the target itself) that must be in `expanded`
// for targetKey to be visible — used to auto-reveal a problem sensor/group
// once its device's subtree has just been lazy-loaded.
export const findAncestorKeys = (nodes: ExplorerNode[], targetKey: string): string[] => {
    const search = (list: ExplorerNode[], ancestors: string[]): string[] | null => {
        for (const node of list) {
            if (node.key === targetKey) {
                return ancestors;
            }
            if (node.children) {
                const found = search(node.children, [...ancestors, node.key]);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };
    return search(nodes, []) || [];
};

// `sites` gets refetched by app-wide code entirely unrelated to the
// explorer (e.g. HeaderNotifications shadow-refreshes the sites list on
// every incoming notification, to keep other pages' alarm indicators
// fresh). Rebuilding from scratch on every such refresh would silently
// discard any device's already-lazy-loaded sensor subtree — from the
// user's point of view, an expanded device would go blank the next time
// any unrelated notification arrived anywhere in the app. `previousNodes`
// lets already-loaded children carry over across a rebuild.
export const buildSiteTree = (sites: Site[], previousNodes: ExplorerNode[] = []): ExplorerNode[] => {
    const previousByKey = new Map<string, ExplorerNode>();
    const collect = (nodes: ExplorerNode[]) => {
        nodes.forEach((node) => {
            previousByKey.set(node.key, node);
            if (node.children) {
                collect(node.children);
            }
        });
    };
    collect(previousNodes);

    return (sites || []).map((site) => {
        const locationId = site.id ?? 0;
        const children = (site.devices || []).map((device) => {
            const key = `device-${device.id}`;
            const previous = previousByKey.get(key);
            return {
                key,
                label: device.title ?? "",
                nodeType: "device" as const,
                locationId,
                deviceId: device.id,
                children: previous?.children,
                childCount: previous?.childCount,
            };
        });
        return {
            key: `site-${site.id}`,
            label: site.title ?? "",
            nodeType: "site" as const,
            locationId,
            childCount: children.length,
            children,
        };
    });
};
